import { useEffect, useState, type FormEvent } from "react";
import { formatIdr } from "../lib/pricing";

const ADMIN_KEY_STORAGE = "rupapreview-price-admin-key";

type PricingResponse = {
  amountIdr: number;
  updatedAt: string | null;
  error?: string;
};

function formatUpdatedAt(value: string | null) {
  if (!value) return "Default price";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jakarta",
    timeZoneName: "short",
  }).format(date);
}

export default function PriceAdminPage() {
  const [key, setKey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [amount, setAmount] = useState("15000");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadPricing(candidate: string) {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch("/api/admin/pricing", {
        headers: { "x-editor-key": candidate },
        cache: "no-store",
      });
      const result = await response.json() as PricingResponse;
      if (!response.ok) throw new Error(result.error || "Could not load pricing.");
      window.sessionStorage.setItem(ADMIN_KEY_STORAGE, candidate);
      setAuthenticated(true);
      setAmount(String(result.amountIdr));
      setUpdatedAt(result.updatedAt);
      return true;
    } catch (cause) {
      window.sessionStorage.removeItem(ADMIN_KEY_STORAGE);
      setAuthenticated(false);
      setError(cause instanceof Error ? cause.message : "Could not load pricing.");
      return false;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const remembered = window.sessionStorage.getItem(ADMIN_KEY_STORAGE) || "";
    if (remembered) {
      setKey(remembered);
      void loadPricing(remembered);
    }
  }, []);

  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (key) await loadPricing(key);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch("/api/admin/pricing", {
        method: "PUT",
        headers: { "content-type": "application/json", "x-editor-key": key },
        body: JSON.stringify({ amountIdr: Number(amount) }),
      });
      const result = await response.json() as PricingResponse;
      if (!response.ok) throw new Error(result.error || "Could not save pricing.");
      setAmount(String(result.amountIdr));
      setUpdatedAt(result.updatedAt);
      setMessage(`Price saved. New checkouts will use ${formatIdr(result.amountIdr)}.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save pricing.");
    } finally {
      setSaving(false);
    }
  }

  function lock() {
    window.sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAuthenticated(false);
    setKey("");
    setMessage("");
    setError("");
  }

  return (
    <main className="admin-page">
      <header className="legal-header">
        <a className="brand" href="/" aria-label="RupaPreview home">
          <img className="brand-logo" src="/rupaworks-logo.png" alt="Rupaworks" />
          <span className="brand-divider" aria-hidden="true" />
          <span className="brand-product"><span>Rupa</span><strong>Preview</strong></span>
        </a>
        {authenticated
          ? <button className="admin-lock" type="button" onClick={lock}>Lock CMS</button>
          : <a className="legal-header__back" href="/">Back to editor ↑</a>}
      </header>

      <section className="admin-main" aria-labelledby="price-cms-title">
        <span className="section-kicker">RUPAPREVIEW CMS</span>
        <h1 id="price-cms-title">Pricing</h1>
        <p className="admin-intro">One setting for Basic Create Link. The public editor, Terms, and new checkout amounts all read this value.</p>

        {!authenticated ? (
          <form className="admin-unlock" onSubmit={unlock}>
            <label htmlFor="admin-key">Admin key</label>
            <input
              id="admin-key"
              type="password"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
            <button className="admin-save" type="submit" disabled={loading || !key}>
              {loading ? "Unlocking…" : "Unlock pricing"}
            </button>
          </form>
        ) : (
          <form className="admin-price-form" onSubmit={save}>
            <div className="admin-current">
              <span>Current price</span>
              <strong>{formatIdr(Number(amount) || 0)}</strong>
              <small>{formatUpdatedAt(updatedAt)}</small>
            </div>
            <label htmlFor="link-price">Basic Create Link · IDR</label>
            <div className="admin-price-input">
              <span>Rp</span>
              <input
                id="link-price"
                type="number"
                min="1"
                max="100000000"
                step="1"
                inputMode="numeric"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                required
                disabled={saving}
              />
            </div>
            <p className="admin-note">New checkouts use the saved price. Existing checkouts keep the amount stored when they were created.</p>
            <button className="admin-save" type="submit" disabled={saving || Number(amount) < 1}>
              {saving ? "Saving…" : "Save price"}
            </button>
          </form>
        )}

        {message && <p className="admin-status admin-status--success" role="status">{message}</p>}
        {error && <p className="admin-status admin-status--error" role="alert">{error}</p>}
      </section>
    </main>
  );
}
